import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Receipt, Plus, Pencil, Trash2, Mail, Send, Download, FileText, X, Bell } from "lucide-react";

const STATUS_META = {
  sent: { label: "Sent", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-800 border-red-200" },
};
const money = (amt, cur) => (amt == null ? "—" : `${cur || "USD"} ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (d) => (d ? d : "—");

export default function ConsoleInvoices() {
  const [data, setData] = useState({ invoices: [], summary: {}, entities: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [editing, setEditing] = useState(null); // invoice obj or {} for new
  const [emailing, setEmailing] = useState(null); // invoice obj
  const [renewalDays, setRenewalDays] = useState(30);
  const [renewalInput, setRenewalInput] = useState("30");
  const [savingRenewal, setSavingRenewal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (entityFilter !== "all") params.registration_id = entityFilter;
      const { data } = await api.get("/console/invoices", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load invoices");
    } finally { setLoading(false); }
  }, [statusFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/console/renewal-settings").then(({ data }) => {
      setRenewalDays(data.days); setRenewalInput(String(data.days));
    }).catch(() => {});
  }, []);

  const saveRenewal = async () => {
    setSavingRenewal(true);
    try {
      const { data } = await api.put("/console/renewal-settings", { days: Number(renewalInput) || 0 });
      setRenewalDays(data.days); setRenewalInput(String(data.days));
      toast.success(data.days === 0 ? "Renewal reminders turned off" : `Reminder set to ${data.days} days before term end`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSavingRenewal(false); }
  };

  const exportCsv = async () => {
    try { await downloadBlob("/console/invoices/csv", "ClaroShield_Invoices.csv"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "CSV export failed"); }
  };

  const downloadPdf = async (inv) => {
    try { await downloadBlob(`/console/invoices/${inv.id}/pdf`, `ClaroShield_Invoice_${inv.invoice_number || inv.id}.pdf`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate PDF"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number || "(no #)"} for ${inv.entity_name}? This also refreshes the entity's access-expiry.`)) return;
    try {
      await api.delete(`/console/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const s = data.summary || {};

  return (
    <div className="space-y-5" data-testid="console-invoices-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><Receipt size={20} className="text-primary" /> Invoices & Billing Ledger</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Manual invoice tracking for every entity. Setting a <b>Paid-through</b> date updates that entity's access term.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} className="rounded-none" data-testid="invoices-export-csv-btn">
            <Download size={16} className="mr-1.5" /> Export CSV
          </Button>
          <Button onClick={() => setEditing({})} className="rounded-none" data-testid="invoices-new-btn">
            <Plus size={16} className="mr-1.5" /> New invoice
          </Button>
        </div>
      </div>

      {/* Renewal reminder setting */}
      <div className="flex flex-wrap items-center gap-2 border border-border bg-muted/40 px-3 py-2" data-testid="renewal-settings">
        <Bell size={16} className="text-amber-600" />
        <span className="text-sm">Email a renewal reminder</span>
        <Input type="number" min="0" max="180" value={renewalInput} onChange={(e) => setRenewalInput(e.target.value)}
          className="rounded-none h-8 w-20 text-sm" data-testid="renewal-days-input" />
        <span className="text-sm">days before an entity's term ends <span className="text-muted-foreground">(0 = off; sent to entity admins + platform ops)</span></span>
        <Button size="sm" variant="secondary" className="rounded-none h-8" onClick={saveRenewal} disabled={savingRenewal || String(renewalDays) === renewalInput} data-testid="renewal-save-btn">
          {savingRenewal ? "Saving…" : "Save"}
        </Button>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="invoices-summary">
        <SummaryChip label="Invoices" value={s.total ?? 0} />
        <SummaryChip label="Sent" value={s.sent ?? 0} tone="amber" />
        <SummaryChip label="Paid" value={s.paid ?? 0} tone="emerald" />
        <SummaryChip label="Overdue" value={s.overdue ?? 0} tone="red" />
        <SummaryChip label="Outstanding" value={`$${Number(s.outstanding_amount || 0).toLocaleString()}`} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none w-40 h-9 text-sm" data-testid="invoices-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="rounded-none w-56 h-9 text-sm" data-testid="invoices-filter-entity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {(data.entities || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Spreadsheet */}
      <div className="border border-border overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="p-3">Entity</th>
              <th className="p-3">PO #</th>
              <th className="p-3">Invoice #</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3">Issue date</th>
              <th className="p-3">Paid-through</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && (data.invoices || []).length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm" data-testid="invoices-empty">
                No invoices yet. Click <b>New invoice</b> to log the first PO/invoice.
              </td></tr>
            )}
            {!loading && (data.invoices || []).map((inv) => {
              const meta = STATUS_META[inv.status] || STATUS_META.sent;
              return (
                <tr key={inv.id} className="border-t border-border hover:bg-muted/40" data-testid={`invoice-row-${inv.id}`}>
                  <td className="p-3 font-medium">{inv.entity_name || "—"}</td>
                  <td className="p-3 font-mono text-xs">{inv.po_number || "—"}</td>
                  <td className="p-3 font-mono text-xs">{inv.invoice_number || "—"}</td>
                  <td className="p-3 text-right tabular-nums">{money(inv.amount, inv.currency)}</td>
                  <td className="p-3 text-xs">{fmtDate(inv.issue_date)}</td>
                  <td className="p-3 text-xs">{fmtDate(inv.paid_through)}</td>
                  <td className="p-3">
                    <span className={`px-1.5 py-0.5 border rounded-sm text-xs font-semibold ${meta.cls}`} data-testid={`invoice-status-${inv.id}`}>{meta.label}</span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Download PDF" onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.id}`}><FileText size={15} /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Email invoice" onClick={() => setEmailing(inv)} data-testid={`invoice-email-${inv.id}`}><Mail size={15} /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => setEditing(inv)} data-testid={`invoice-edit-${inv.id}`}><Pencil size={15} /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" title="Delete" onClick={() => remove(inv)} data-testid={`invoice-delete-${inv.id}`}><Trash2 size={15} /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <InvoiceDialog invoice={editing} entities={data.entities || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {emailing && <EmailDialog invoice={emailing} onClose={() => setEmailing(null)} />}
    </div>
  );
}

function SummaryChip({ label, value, tone }) {
  const tones = { amber: "text-amber-700", emerald: "text-emerald-700", red: "text-red-700" };
  return (
    <div className="border border-border bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${tones[tone] || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function InvoiceDialog({ invoice, entities, onClose, onSaved }) {
  const isNew = !invoice.id;
  const [f, setF] = useState({
    registration_id: invoice.registration_id || "",
    po_number: invoice.po_number || "",
    invoice_number: invoice.invoice_number || "",
    currency: invoice.currency || "USD",
    issue_date: invoice.issue_date || "",
    paid_through: invoice.paid_through || "",
    status: invoice.status || "sent",
  });
  const [items, setItems] = useState(() => {
    if (invoice.line_items && invoice.line_items.length) {
      return invoice.line_items.map((i) => ({ description: i.description || "", quantity: i.quantity ?? 1, unit_price: i.unit_price ?? 0 }));
    }
    if (invoice.id && invoice.amount != null) {
      return [{ description: "Services", quantity: 1, unit_price: invoice.amount }];
    }
    return [{ description: "", quantity: 1, unit_price: "" }];
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setItem = (idx, k, v) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((s) => [...s, { description: "", quantity: 1, unit_price: "" }]);
  const removeItem = (idx) => setItems((s) => s.filter((_, i) => i !== idx));
  const lineTotal = (it) => (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  const total = items.reduce((sum, it) => sum + lineTotal(it), 0);

  const save = async () => {
    if (isNew && !f.registration_id) { toast.error("Pick an entity"); return; }
    setSaving(true);
    try {
      const line_items = items
        .filter((it) => (it.description || "").trim() || Number(it.unit_price) || Number(it.quantity))
        .map((it) => ({ description: it.description || "", quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 }));
      const payload = {
        po_number: f.po_number, invoice_number: f.invoice_number, line_items,
        currency: f.currency, issue_date: f.issue_date, paid_through: f.paid_through, status: f.status,
      };
      if (isNew) {
        await api.post("/console/invoices", { registration_id: f.registration_id, ...payload });
        toast.success("Invoice added");
      } else {
        await api.patch(`/console/invoices/${invoice.id}`, payload);
        toast.success("Invoice updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt size={18} /> {isNew ? "New invoice" : "Edit invoice"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs">Entity</Label>
            {isNew ? (
              <Select value={f.registration_id} onValueChange={(v) => set("registration_id", v)}>
                <SelectTrigger className="rounded-none" data-testid="invoice-field-entity"><SelectValue placeholder="Select an entity…" /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="font-medium py-2">{invoice.entity_name}</div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">PO #</Label>
              <Input value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className="rounded-none" data-testid="invoice-field-po" />
            </div>
            <div>
              <Label className="text-xs">Invoice #</Label>
              <Input value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className="rounded-none" data-testid="invoice-field-invnum" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Currency</Label>
              <Input value={f.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} className="rounded-none uppercase" data-testid="invoice-field-currency" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Line items</Label>
              <Button type="button" size="sm" variant="ghost" className="h-7 rounded-none text-xs" onClick={addItem} data-testid="invoice-add-item"><Plus size={13} className="mr-1" /> Add line</Button>
            </div>
            <div className="space-y-1.5">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Description" className="rounded-none flex-1 h-8 text-xs" data-testid={`invoice-item-desc-${idx}`} />
                  <Input type="number" min="0" step="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} placeholder="Qty" className="rounded-none w-16 h-8 text-xs" data-testid={`invoice-item-qty-${idx}`} />
                  <Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} placeholder="Unit $" className="rounded-none w-24 h-8 text-xs" data-testid={`invoice-item-price-${idx}`} />
                  <div className="w-24 text-right text-xs tabular-nums text-slate-600">{lineTotal(it).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeItem(idx)} disabled={items.length <= 1} data-testid={`invoice-item-remove-${idx}`}><X size={14} /></Button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2 border-t border-border pt-2">
              <span className="text-xs text-muted-foreground">Total</span>
              <span className="text-base font-bold tabular-nums" data-testid="invoice-total">{f.currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Invoice / issue date</Label>
              <Input type="date" value={f.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="rounded-none" data-testid="invoice-field-issue" />
            </div>
            <div>
              <Label className="text-xs">Paid-through (access term)</Label>
              <Input type="date" value={f.paid_through} onChange={(e) => set("paid_through", e.target.value)} className="rounded-none" data-testid="invoice-field-paidthrough" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="rounded-none" data-testid="invoice-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">A Paid-through date sets this entity's access-expiry to the latest paid-through across its invoices.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="invoice-save-btn">{saving ? "Saving…" : "Save invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailDialog({ invoice, onClose }) {
  const [admins, setAdmins] = useState([]);
  const [checked, setChecked] = useState({}); // email -> bool
  const [extra, setExtra] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/console/invoices/${invoice.id}/recipients`)
      .then(({ data }) => {
        setAdmins(data.admins || []);
        const init = {};
        (data.admins || []).forEach((a) => { init[a.email] = true; });
        setChecked(init);
      })
      .catch(() => setAdmins([]));
  }, [invoice.id]);

  const recipients = useMemo(() => {
    const picked = admins.filter((a) => checked[a.email]).map((a) => a.email);
    const extras = extra.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
    return Array.from(new Set([...picked, ...extras]));
  }, [admins, checked, extra]);

  const send = async () => {
    if (recipients.length === 0) { toast.error("Add at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/console/invoices/${invoice.id}/email`, { recipients, message });
      if (data.delivered) toast.success(`Invoice emailed to ${recipients.length} recipient(s)`);
      else toast.warning("Send attempted, but delivery could not be confirmed (check the recipient domain / email config).");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="invoice-email-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail size={18} /> Email invoice — {invoice.entity_name}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-muted/50 border border-border p-3 text-xs space-y-0.5">
            {invoice.invoice_number && <div>Invoice #: <b>{invoice.invoice_number}</b></div>}
            {invoice.po_number && <div>PO #: <b>{invoice.po_number}</b></div>}
            <div>Amount: <b>{money(invoice.amount, invoice.currency)}</b></div>
            {invoice.paid_through && <div>Paid-through: <b>{invoice.paid_through}</b></div>}
          </div>
          <div>
            <Label className="text-xs">Entity admins (uncheck to exclude)</Label>
            {admins.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No entity admins found — add an A/R or procurement address below.</p>
            ) : (
              <div className="space-y-1.5 mt-1">
                {admins.map((a) => (
                  <label key={a.email} className="flex items-center gap-2 cursor-pointer" data-testid={`invoice-email-recipient-${a.email}`}>
                    <Checkbox checked={!!checked[a.email]} onCheckedChange={(v) => setChecked((s) => ({ ...s, [a.email]: !!v }))} />
                    <span className="text-sm">{a.name ? `${a.name} · ` : ""}<span className="font-mono text-xs">{a.email}</span></span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Add A/R or procurement addresses (comma-separated)</Label>
            <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="ap@customer.com, procurement@customer.com" className="rounded-none" data-testid="invoice-email-extra" />
          </div>
          <div>
            <Label className="text-xs">Optional message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="e.g. Net 30 terms. Please reference the PO number on remittance." className="rounded-none" data-testid="invoice-email-message" />
          </div>
          <p className="text-[11px] text-muted-foreground">Sending to: {recipients.length ? recipients.join(", ") : "—"}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={sending || recipients.length === 0} data-testid="invoice-email-send-btn">
            <Send size={15} className="mr-1.5" /> {sending ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
